import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CreditCard,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  Filter,
  DollarSign,
  ArrowLeft,
  User,
  LogOut
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import Loading from '../components/common/Loading';
import Alert from '../components/common/Alert';
import Badge from '../components/common/Badge';
import { Receivable } from '../types';
import { receivableService } from '../services/receivableService';
import { formatCurrency, formatDate } from '../utils/formatters';

const MyFinancial: React.FC = () => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const [receivables, setReceivables] = useState<Receivable[]>([]);
  const [filteredReceivables, setFilteredReceivables] = useState<Receivable[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  useEffect(() => {
    loadReceivables();
  }, []);

  useEffect(() => {
    filterReceivables();
  }, [statusFilter, receivables]);

  const loadReceivables = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await receivableService.getMyReceivables();
      setReceivables(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao carregar histórico financeiro');
    } finally {
      setLoading(false);
    }
  };

  const filterReceivables = () => {
    if (statusFilter === 'all') {
      setFilteredReceivables(receivables);
    } else {
      setFilteredReceivables(receivables.filter(r => r.status === statusFilter));
    }
  };

  const getStatusInfo = (status: string) => {
    switch (status) {
      case 'paid':
        return {
          label: 'Paga',
          variant: 'success' as const,
          icon: <CheckCircle className="w-5 h-5" />,
          color: 'text-green-600',
        };
      case 'pending':
        return {
          label: 'Pendente',
          variant: 'warning' as const,
          icon: <Clock className="w-5 h-5" />,
          color: 'text-yellow-600',
        };
      case 'overdue':
        return {
          label: 'Vencida',
          variant: 'danger' as const,
          icon: <AlertCircle className="w-5 h-5" />,
          color: 'text-red-600',
        };
      case 'cancelled':
        return {
          label: 'Cancelada',
          variant: 'secondary' as const,
          icon: <XCircle className="w-5 h-5" />,
          color: 'text-gray-600',
        };
      default:
        return {
          label: status,
          variant: 'secondary' as const,
          icon: <Clock className="w-5 h-5" />,
          color: 'text-gray-600',
        };
    }
  };

  const calculateTotals = () => {
    const paid = filteredReceivables
      .filter(r => r.status === 'paid')
      .reduce((sum, r) => sum + (Number(r.amount_paid) || 0), 0);

    const pending = filteredReceivables
      .filter(r => r.status === 'pending')
      .reduce((sum, r) => sum + (Number(r.amount) || 0), 0);

    const overdue = filteredReceivables
      .filter(r => r.status === 'overdue')
      .reduce((sum, r) => sum + (Number(r.amount) || 0), 0);

    return { paid, pending, overdue };
  };

  const totals = calculateTotals();

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loading />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-3">
              <button
                type="button"
                title="Voltar"
                onClick={() => navigate('/meus-parceiros')}
                className="w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center justify-center transition-colors flex-shrink-0"
              >
                <ArrowLeft className="w-5 h-5 text-gray-700" />
              </button>
              <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-green-700 rounded-lg flex items-center justify-center flex-shrink-0">
                <CreditCard className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Meu Histórico Financeiro</h1>
                <p className="text-sm text-gray-600">Acompanhe suas parcelas e pagamentos</p>
              </div>
            </div>

            <div className="flex items-center gap-2 sm:gap-4">
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <User className="w-4 h-4" />
                <span className="truncate max-w-[120px] sm:max-w-none">{user?.name}</span>
              </div>
              <button
                type="button"
                onClick={signOut}
                className="flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span>Sair</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">

      {error && (
        <Alert variant="error" onClose={() => setError('')} className="mb-6">
          {error}
        </Alert>
      )}

      {/* Cards de resumo */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-green-50 border border-green-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-green-700 font-medium">Pagas</span>
            <CheckCircle className="w-5 h-5 text-green-600" />
          </div>
          <p className="text-2xl font-bold text-green-900">{formatCurrency(totals.paid)}</p>
          <p className="text-sm text-green-600 mt-1">
            {filteredReceivables.filter(r => r.status === 'paid').length} parcela(s)
          </p>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-yellow-700 font-medium">Pendentes</span>
            <Clock className="w-5 h-5 text-yellow-600" />
          </div>
          <p className="text-2xl font-bold text-yellow-900">{formatCurrency(totals.pending)}</p>
          <p className="text-sm text-yellow-600 mt-1">
            {filteredReceivables.filter(r => r.status === 'pending').length} parcela(s)
          </p>
        </div>

        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-red-700 font-medium">Vencidas</span>
            <AlertCircle className="w-5 h-5 text-red-600" />
          </div>
          <p className="text-2xl font-bold text-red-900">{formatCurrency(totals.overdue)}</p>
          <p className="text-sm text-red-600 mt-1">
            {filteredReceivables.filter(r => r.status === 'overdue').length} parcela(s)
          </p>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex items-center gap-4">
          <Filter className="w-5 h-5 text-gray-500" />
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setStatusFilter('all')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                statusFilter === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Todas
            </button>
            <button
              onClick={() => setStatusFilter('paid')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                statusFilter === 'paid'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Pagas
            </button>
            <button
              onClick={() => setStatusFilter('pending')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                statusFilter === 'pending'
                  ? 'bg-yellow-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Pendentes
            </button>
            <button
              onClick={() => setStatusFilter('overdue')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                statusFilter === 'overdue'
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Vencidas
            </button>
          </div>
        </div>
      </div>

      {/* Lista de parcelas */}
      {filteredReceivables.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <CreditCard className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            Nenhuma parcela encontrada
          </h3>
          <p className="text-gray-600">
            {statusFilter === 'all'
              ? 'Você ainda não possui parcelas cadastradas'
              : `Você não possui parcelas com status "${getStatusInfo(statusFilter).label}"`}
          </p>
        </div>
      ) : (
        <>
        {/* Mobile: Cards */}
        <div className="md:hidden space-y-4">
          {filteredReceivables.map((receivable) => {
            const statusInfo = getStatusInfo(receivable.status);
            return (
              <div key={receivable.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-gray-400" />
                    <span className="text-sm font-medium text-gray-900">
                      Parcela #{receivable.installment_number || '-'}
                    </span>
                  </div>
                  <Badge variant={statusInfo.variant}>
                    {statusInfo.label}
                  </Badge>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-gray-500">Plano</p>
                    <p className="font-medium text-gray-900">{receivable.plan?.nome || '-'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Valor</p>
                    <p className="font-medium text-gray-900">{formatCurrency(receivable.amount)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Vencimento</p>
                    <p className="font-medium text-gray-900">{formatDate(receivable.due_date)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Pagamento</p>
                    <p className="font-medium text-gray-900">
                      {receivable.payment_date ? formatDate(receivable.payment_date) : '-'}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Desktop: Tabela */}
        <div className="hidden md:block bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Parcela
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Plano
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Vencimento
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Valor
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Pagamento
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredReceivables.map((receivable) => {
                  const statusInfo = getStatusInfo(receivable.status);
                  return (
                    <tr key={receivable.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <CreditCard className="w-5 h-5 text-gray-400 mr-2" />
                          <span className="text-sm font-medium text-gray-900">
                            Parcela #{receivable.installment_number || '-'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-900">
                          {receivable.plan?.nome || '-'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-900">
                          {formatDate(receivable.due_date)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <DollarSign className="w-4 h-4 text-gray-400 mr-1" />
                          <span className="text-sm font-medium text-gray-900">
                            {formatCurrency(receivable.amount)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Badge variant={statusInfo.variant}>
                          {statusInfo.label}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {receivable.payment_date
                          ? formatDate(receivable.payment_date)
                          : '-'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        </>
      )}
      </div>
    </div>
  );
};

export default MyFinancial;
